import { ProcessStepForm } from '@/components/processes/ProcessStepForm';
import {
  Button,
  ConfirmDialog,
  EmptyTableState,
} from '@startupquickstart/frontend';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { CSS } from '@dnd-kit/utilities';
import {
  ChevronDown,
  ChevronUp,
  GripVertical,
  ListChecks,
  Pencil,
  Trash2,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

/** A single draft step held in local form state before the process is created. */
export interface DraftStep {
  /** Local ID — used by dnd-kit. Not persisted; the server assigns a real UUID. */
  id: string;
  body: string;
}

interface DraftStepsEditorProps {
  /** Controlled list of draft steps (in display order). */
  value: DraftStep[];
  /** Called whenever the list changes (add, edit, delete, reorder). */
  onChange: (next: DraftStep[]) => void;
}

// ---------------------------------------------------------------------------
// Inline edit cell
// ---------------------------------------------------------------------------

interface InlineEditCellProps {
  value: string;
  onSave: (value: string) => void;
}

function InlineEditCell({ value, onSave }: InlineEditCellProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  const commit = () => {
    const trimmed = draft.trim();
    if (!trimmed || trimmed === value) {
      setEditing(false);
      setDraft(value);
      return;
    }
    onSave(trimmed);
    setEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commit();
    } else if (e.key === 'Escape') {
      setEditing(false);
      setDraft(value);
    }
  };

  if (editing) {
    return (
      <input
        ref={inputRef}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={handleKeyDown}
        className="flex-1 min-w-0 text-sm border rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-primary bg-background"
        aria-label="Edit step text"
      />
    );
  }

  return (
    <button
      type="button"
      onClick={() => { setEditing(true); setDraft(value); }}
      title="Click to edit"
      className="flex-1 min-w-0 text-left text-sm text-foreground leading-relaxed group/edit flex items-center gap-1.5 cursor-text"
    >
      <span className="whitespace-pre-wrap break-words">{value}</span>
      <Pencil className="h-3 w-3 shrink-0 opacity-0 group-hover/edit:opacity-50 transition-opacity" />
    </button>
  );
}

// ---------------------------------------------------------------------------
// Sortable row
// ---------------------------------------------------------------------------

interface SortableDraftRowProps {
  step: DraftStep;
  index: number;
  total: number;
  onBodySave: (id: string, body: string) => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onDelete: () => void;
}

function SortableDraftRow({
  step, index, total, onBodySave, onMoveUp, onMoveDown, onDelete,
}: SortableDraftRowProps) {
  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({ id: step.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : undefined,
    opacity: isDragging ? 0.8 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-start gap-3 px-4 py-3 ${isDragging ? 'bg-muted shadow-lg rounded-lg' : ''}`}
    >
      <button
        type="button"
        className="touch-none shrink-0 cursor-grab active:cursor-grabbing text-muted-foreground/60 hover:text-muted-foreground mt-0.5"
        {...attributes}
        {...listeners}
        aria-label="Drag to reorder"
      >
        <GripVertical className="h-4 w-4" />
      </button>

      <span className="flex-shrink-0 h-5 w-5 mt-0.5 rounded-full bg-muted flex items-center justify-center text-xs font-medium text-muted-foreground">
        {index + 1}
      </span>

      <InlineEditCell value={step.body} onSave={(body) => onBodySave(step.id, body)} />

      <div className="flex items-center gap-0.5">
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          disabled={index === 0}
          onClick={onMoveUp}
          aria-label="Move step up"
          type="button"
        >
          <ChevronUp className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          disabled={index === total - 1}
          onClick={onMoveDown}
          aria-label="Move step down"
          type="button"
        >
          <ChevronDown className="h-4 w-4" />
        </Button>
      </div>

      <Button
        variant="ghost"
        size="icon"
        className="h-7 w-7 text-destructive hover:bg-destructive/10"
        onClick={onDelete}
        aria-label="Delete step"
        type="button"
      >
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main editor (controlled, no API calls)
// ---------------------------------------------------------------------------

/**
 * Local steps editor used in the process create form, before the parent
 * process exists in the database. Pure controlled component — no API calls.
 * The parent form is responsible for persisting these steps after creating
 * the process.
 */
export function DraftStepsEditor({ value, onChange }: DraftStepsEditorProps) {
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = value.findIndex((s) => s.id === active.id);
    const newIndex = value.findIndex((s) => s.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const next = [...value];
    const [moved] = next.splice(oldIndex, 1);
    next.splice(newIndex, 0, moved);
    onChange(next);
  }, [value, onChange]);

  const moveStep = useCallback((id: string, direction: 'up' | 'down') => {
    const idx = value.findIndex((s) => s.id === id);
    if (idx === -1) return;
    const target = direction === 'up' ? idx - 1 : idx + 1;
    if (target < 0 || target >= value.length) return;

    const next = [...value];
    const [moved] = next.splice(idx, 1);
    next.splice(target, 0, moved);
    onChange(next);
  }, [value, onChange]);

  const handleAdd = async (body: string) => {
    const id =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `draft-${Date.now()}-${Math.random()}`;
    onChange([...value, { id, body }]);
  };

  const handleBodySave = (id: string, body: string) => {
    onChange(value.map((s) => (s.id === id ? { ...s, body } : s)));
  };

  const handleDeleteConfirm = () => {
    if (!deleteTarget) return;
    onChange(value.filter((s) => s.id !== deleteTarget));
    setDeleteTarget(null);
  };

  const deleteTargetStep = value.find((s) => s.id === deleteTarget);

  return (
    <div className="space-y-3">
      {value.length === 0 ? (
        <EmptyTableState
          icon={<ListChecks className="h-5 w-5" />}
          title="No steps yet"
          description="Add a step below — it will be saved when you create the process"
        />
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          modifiers={[restrictToVerticalAxis]}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={value.map((s) => s.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="divide-y rounded-lg border bg-background">
              {value.map((step, idx) => (
                <SortableDraftRow
                  key={step.id}
                  step={step}
                  index={idx}
                  total={value.length}
                  onBodySave={handleBodySave}
                  onMoveUp={() => moveStep(step.id, 'up')}
                  onMoveDown={() => moveStep(step.id, 'down')}
                  onDelete={() => setDeleteTarget(step.id)}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      <ProcessStepForm onSubmit={handleAdd} isPending={false} />

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}
        title="Delete Step"
        description={
          deleteTargetStep
            ? `Delete step: "${deleteTargetStep.body}"?`
            : 'Delete this step?'
        }
        confirmText="Delete"
        variant="destructive"
        onConfirm={handleDeleteConfirm}
      />
    </div>
  );
}
